/**
 * BookingGuestFormDrawer
 *
 * Reusable full-featured guest form drawer extracted from CheckInDrawer.
 * Supports two persist modes:
 *   Mode A – direct:  pass bookingItemIds[]  (EditBookingItems, walk-in add)
 *   Mode B – complex: pass resolveItemsToUpdate() callback (CheckIn full flow)
 *   Mode B – defer:   pass deferGuestPersist=true + onDeferredSave() (compact check-in room selection)
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Grid,
  Chip,
  Paper,
  Divider,
  TextField,
  InputAdornment,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  useTheme,
  Menu,
  ListItemIcon,
  ListItemText,
  Popper,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PublicIcon from '@mui/icons-material/Public';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDatePicker } from '../../shared/components/forms/HotelDatePicker';
import api, { searchGuestByPhone, getCustomerDetail, getBookingGuestDocuments } from '../../services/api';
import countriesData from '../../data/countries-states-cities-data';
import countryCodesData from '../../data/country-codes.json';
import WalkInAiCheckinOverlay from '../WalkInAiCheckinOverlay';
import ForeignGuestDetailsDrawer from './ForeignGuestDetailsDrawer';
import {
  EMPTY_FOREIGN_GUEST_DETAILS,
  applyForeignCountryChange,
  foreignDetailsToGuestApiPayload,
  isForeignGuestCountry,
  mapGuestApiToForeignDetails,
  validateForeignGuestDetails,
} from '../../utils/foreignGuestFormUtils';
import { BookingDrawerCodeChip } from './DrawerEntityLink';
import { AiGradientButton } from '../../shared';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getGuestDrawerContentSx,
  getGuestDrawerUploadPaperSx,
  getGuestDrawerMetaChipSx,
  createBookingDrawerOverlayProps,
} from '../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import {
  countryFilterOptions,
  stateFilterOptions,
  cityFilterOptions,
} from '../../utils/locationSearch';
import {
  formatAadhaarDisplay,
  isAadhaarDocumentType,
  normalizeDocumentIdForSubmit,
  normalizeDocumentIdInput,
  validateAadhaarIdNumber,
  validateGuestDocumentImages,
} from '../../utils/documentIdFormatters';
import {
  formatPhoneLengthLabel,
  getPhoneMaxLength,
  normalizePhoneInputForCountry,
  validatePhoneForCountry,
} from '../../utils/phoneValidation';
import {
  captureNativeCameraDataUrl,
  getNativeCameraErrorMessage,
} from '../../utils/nativeCameraCapture';
import { isGuestChild } from '../../utils/bookingUtils';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

// ─── Constants (exported so callers can reuse) ───────────────────────────────

const CUSTOM_BORDER_COLOR = '#4ce1b6';

export const NO_DOCUMENT_VALUE = 'no_document';

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const documentUploadOptions = [
  { value: NO_DOCUMENT_VALUE, label: 'No Document' },
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'voter_id', label: 'Voter ID (EPIC)' },
  { value: 'driving_license', label: 'Driving Licence' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'government_id', label: 'Government Issued ID' },
  { value: 'oci', label: 'OCI Card' },
  { value: 'pio', label: 'PIO Card' },
  { value: 'student_id', label: 'Student ID' },
  { value: 'company_id', label: 'Corporate / Company ID' },
  { value: 'other', label: 'Other Document' },
];

const severityOptions = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const titleOptions = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
  { value: 'Sir', label: 'Sir' },
  { value: 'Madam', label: 'Madam' },
];

/** Resolve the hotel's country name from window.DJANGO_CONTEXT */
export const resolveHotelCountryName = () => {
  const hotel =
    (typeof window !== 'undefined' && window.DJANGO_CONTEXT?.selectedHotel) || null;
  if (!hotel?.country) return '';
  const raw = String(hotel.country).trim();
  let obj = countriesData.countries.find((c) => c.code === raw || c.name === raw);
  if (!obj) {
    obj = countriesData.countries.find(
      (c) =>
        c.code?.toLowerCase() === raw.toLowerCase() ||
        c.name?.toLowerCase() === raw.toLowerCase(),
    );
  }
  return obj?.name || raw;
};

/** Returns an empty guest form with hotel-country default */
export const createEmptyGuestForm = () => ({
  title: '',
  name: '',
  first_name: '',
  middle_name: '',
  last_name: '',
  email: '',
  phone: '',
  phone_country_code: '+91',
  dob: '',
  gender: '',
  address: '',
  city: '',
  state: '',
  country: resolveHotelCountryName(),
  zipcode: '',
  camera_upload: false,
  do_not_rent: false,
  do_not_rent_severity: 'HIGH',
  do_not_rent_reason: '',
  id_front: '',
  id_back: '',
  document_type: NO_DOCUMENT_VALUE,
  id_number: '',
});

export const parseFullName = (fullName) => {
  if (!fullName?.trim()) return { first_name: '', middle_name: '', last_name: '' };
  const parts = fullName.trim().replace(/\s+/g, ' ').split(' ').filter(Boolean);
  if (parts.length === 0) return { first_name: '', middle_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], middle_name: '', last_name: '' };
  if (parts.length === 2) return { first_name: parts[0], middle_name: '', last_name: parts[1] };
  if (parts.length === 3) return { first_name: parts[0], middle_name: parts[1], last_name: parts[2] };
  return {
    first_name: parts.slice(0, parts.length - 2).join(' '),
    middle_name: parts[parts.length - 2],
    last_name: parts[parts.length - 1],
  };
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {function} props.onClose
 * @param {string|number} props.bookingId
 * @param {object}   [props.booking]           – optional, for code chip in header
 * @param {string}   [props.roomLabel]          – optional chip, e.g. "Deluxe - 105"
 * @param {number}   [props.editingGuestId]     – null for add, guestId for edit
 * @param {object}   [props.initialGuest]       – prefill when editing (same shape as createEmptyGuestForm)
 * @param {number[]} [props.bookingItemIds]     – Mode A: patch these items directly
 * @param {boolean}  [props.deferGuestPersist]  – Mode B-defer: stage form, don't hit API
 * @param {function} [props.onDeferredSave]     – (stagedForm) => void
 * @param {function} [props.resolveItemsToUpdate] – async () => [{id},...] Mode B-direct
 * @param {function} [props.onSaved]            – async () => void  after API save
 * @param {function} [props.onSuccessMessage]   – optional (msg) => void
 */
const BookingGuestFormDrawer = ({
  open,
  onClose,
  bookingId,
  booking,
  roomLabel,
  editingGuestId: editingGuestIdProp,
  initialGuest,
  bookingItemIds,
  deferGuestPersist,
  onDeferredSave,
  resolveItemsToUpdate,
  onSaved,
  onSuccessMessage,
}) => {
  const { can } = usePermission();
  const canManageAiCheckin = can(PERMISSIONS.AI_CHECKIN_MANAGE);
  // ── Theme ──────────────────────────────────────────────────────────────────
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const theme = useTheme();
  const { selectMenuProps, datePickerSlotProps } = useMemo(
    () => createBookingDrawerOverlayProps(isDark),
    [isDark],
  );

  useEffect(() => {
    const cleanup = watchThemeChanges((t) => setCurrentTheme(t));
    return cleanup;
  }, []);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState(() => createEmptyGuestForm());
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [isPhoneLocked, setIsPhoneLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [guestFormError, setGuestFormError] = useState(null);
  const [aiAddGuestOpen, setAiAddGuestOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [guestSearchOptions, setGuestSearchOptions] = useState([]);
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [titleMenuOpen, setTitleMenuOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [foreignGuestDetails, setForeignGuestDetails] = useState(EMPTY_FOREIGN_GUEST_DETAILS);
  const [foreignGuestDrawerOpen, setForeignGuestDrawerOpen] = useState(false);

  // ── Camera state ───────────────────────────────────────────────────────────
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraType, setCameraType] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraPermissionHint, setCameraPermissionHint] = useState({ open: false, message: '' });

  // ── Refs ───────────────────────────────────────────────────────────────────
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputFrontRef = useRef(null);
  const fileInputBackRef = useRef(null);
  const phoneCountryCodeAnchorRef = useRef(null);
  const phoneInputAnchorRef = useRef(null);
  const titleMenuAnchorRef = useRef(null);
  const phoneSearchDebounceTimerRef = useRef(null);

  // ── Init when drawer opens / cleanup on close ──────────────────────────────
  useEffect(() => {
    if (!open) {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        setStream(null);
      }
      setCameraOpen(false);
      return;
    }
    // Snapshot on open
    setFormData(initialGuest ? { ...initialGuest } : createEmptyGuestForm());
    setEditingGuestId(editingGuestIdProp || null);
    setIsPhoneLocked(Boolean(editingGuestIdProp));
    setValidationErrors({});
    setTouchedFields({});
    setGuestFormError(null);
    setGuestSearchOptions([]);
    setForeignGuestDetails(
      initialGuest
        ? mapGuestApiToForeignDetails(initialGuest, initialGuest.country)
        : EMPTY_FOREIGN_GUEST_DETAILS
    );
    setForeignGuestDrawerOpen(false);
    setPhoneCountryCodeSearch('');
    setPhoneCountryCodeMenuOpen(false);
    setTitleMenuOpen(false);
    setAiAddGuestOpen(false);

    // Auto-fetch signed document image URLs when editing an existing guest
    if (editingGuestIdProp && bookingId && (initialGuest?.has_id_front || initialGuest?.has_id_back)) {
      getBookingGuestDocuments(bookingId, editingGuestIdProp)
        .then((docData) => {
          if (docData.id_front_url || docData.id_back_url || docData.id_number) {
            setFormData((prev) => ({
              ...prev,
              id_front: docData.id_front_url || prev.id_front,
              id_back: docData.id_back_url || prev.id_back,
              id_number: docData.id_number || prev.id_number,
            }));
          }
        })
        .catch(() => {
          // Documents may fail to load — form is still usable without pre-populated images
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (phoneSearchDebounceTimerRef.current)
        clearTimeout(phoneSearchDebounceTimerRef.current);
    };
  }, [stream]);

  // ── Derived / computed ─────────────────────────────────────────────────────
  const isNoDocumentSelected = formData.document_type === NO_DOCUMENT_VALUE;
  const effectiveDocumentType = isNoDocumentSelected ? '' : formData.document_type;
  const isAadhaarDocumentSelected = isAadhaarDocumentType(effectiveDocumentType);
  const normalizedIdNumberValue = isAadhaarDocumentSelected
    ? formatAadhaarDisplay(formData.id_number)
    : formData.id_number || '';

  const countries = useMemo(
    () => countriesData.countries.map((c) => ({ code: c.code, name: c.name })),
    [],
  );
  const selectedCountryObj = useMemo(
    () =>
      !formData.country
        ? null
        : countriesData.countries.find(
            (c) => c.name === formData.country || c.code === formData.country,
          ),
    [formData.country],
  );
  const hotelCountry = useMemo(() => resolveHotelCountryName(), []);
  const isForeignGuest = useMemo(
    () => isForeignGuestCountry(formData.country, hotelCountry),
    [formData.country, hotelCountry]
  );
  const foreignGuestComplete = useMemo(() => {
    if (!isForeignGuest) return true;
    return validateForeignGuestDetails(foreignGuestDetails).ok;
  }, [isForeignGuest, foreignGuestDetails]);

  const applyGuestCountryChange = useCallback((countryValue) => {
    const next = applyForeignCountryChange(countryValue, hotelCountry, foreignGuestDetails);
    setForeignGuestDetails(next.details);
    if (next.shouldOpenDrawer) {
      setFormData((prev) => ({
        ...prev,
        document_type: prev.document_type === NO_DOCUMENT_VALUE ? 'passport' : prev.document_type,
      }));
      setForeignGuestDrawerOpen(true);
    } else {
      setForeignGuestDrawerOpen(false);
    }
    setValidationErrors((prev) => ({ ...prev, country: validateCountry(countryValue), foreignGuest: '' }));
  }, [foreignGuestDetails, hotelCountry]);
  const availableStates = useMemo(
    () =>
      selectedCountryObj
        ? selectedCountryObj.states.map((s) => ({ code: s.code, name: s.name }))
        : [],
    [selectedCountryObj],
  );
  const availableCities = useMemo(() => {
    if (!selectedCountryObj) return [];
    const stateObj = selectedCountryObj.states.find(
      (s) => s.name === formData.state || s.code === formData.state,
    );
    return stateObj?.cities || [];
  }, [selectedCountryObj, formData.state]);

  const countryCodes = useMemo(() => countryCodesData, []);
  const selectedPhoneCountryCode = useMemo(
    () =>
      countryCodes.find((cc) => cc.dialCode === formData.phone_country_code) ||
      countryCodes.find((cc) => cc.code === 'IN'),
    [formData.phone_country_code, countryCodes],
  );

  // ── Document label helpers ──────────────────────────────────────────────────
  const getDocumentTypeLabel = () => {
    const opt = documentUploadOptions.find((o) => o.value === formData.document_type);
    return opt ? opt.label : 'Document';
  };
  const getFrontDocumentLabel = () => `Upload ${getDocumentTypeLabel()} Front`;
  const getBackDocumentLabel = () => `Upload ${getDocumentTypeLabel()} Back`;
  const getDocumentNumberPlaceholder = () => {
    const t = formData.document_type;
    if (t === 'aadhaar') return 'XXXX-XXXX-XXXX';
    if (t === 'passport') return 'Enter passport number (e.g. A1234567)';
    if (t === 'pan') return 'Enter PAN (e.g. ABCDE1234F)';
    if (t === 'voter_id') return 'Enter voter ID (e.g. ABC1234567)';
    if (t === 'driving_license') return 'Enter driving licence number';
    return 'Enter ID number';
  };

  // ── Validation helpers ─────────────────────────────────────────────────────
  const validateEmail = (email) => {
    if (!email?.trim()) return '';
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      ? ''
      : 'Please enter a valid email address';
  };
  const validatePhone = (phone, cc = '+91') => validatePhoneForCountry(phone, cc);
  const validateName = (name) => {
    if (!name?.trim()) return 'Name is required';
    if (name.trim().length < 2) return 'Name must be at least 2 characters';
    if (name.trim().length > 100) return 'Name cannot exceed 100 characters';
    if (!/^[a-zA-Z\s\-'.]+$/.test(name.trim()))
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    return '';
  };
  const validatePincode = (pin) => {
    if (!pin?.trim()) return '';
    const d = pin.replace(/\D/g, '');
    if (d.length < 4) return 'Pincode must be at least 4 digits';
    if (d.length > 10) return 'Pincode cannot exceed 10 digits';
    return '';
  };
  const validateCity = () => '';
  const validateCountry = (country) =>
    !country?.trim() ? 'Country is required' : '';
  const validateDocumentIdNumber = (docType, idNumber) => {
    const dt = (docType || '').trim();
    const raw = (idNumber || '').trim();
    if (!dt && !raw) return '';
    if (!dt && raw) return 'Select document type before entering ID number';
    if (!raw) return 'ID number is required';
    const n = raw.replace(/\s+/g, '').toUpperCase();
    if (dt === 'aadhaar') return validateAadhaarIdNumber(n);
    if (dt === 'passport')
      return /^[A-Z][0-9]{7}$/.test(n)
        ? ''
        : 'Passport number must be 1 letter followed by 7 digits (e.g. A1234567)';
    if (dt === 'voter_id')
      return /^[A-Z]{3}[0-9]{7}$/.test(n)
        ? ''
        : 'Voter ID must be 3 letters followed by 7 digits (e.g. ABC1234567)';
    if (dt === 'pan')
      return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(n)
        ? ''
        : 'PAN must be 5 letters, 4 digits, and 1 letter (e.g. ABCDE1234F)';
    if (dt === 'driving_license')
      return /^[A-Z0-9-]{10,20}$/.test(n) ? '' : 'Driving licence number must be 10-20 characters';
    return /^[A-Z0-9][A-Z0-9\-/]{3,29}$/.test(n)
      ? ''
      : 'ID number must be 4-30 characters (letters, numbers, -, /)';
  };

  const formatGuestSaveError = (raw) => {
    const text = typeof raw === 'string' ? raw : JSON.stringify(raw || '');
    if (
      text.includes('unique_guest_loyalty_per_hotel') ||
      text.includes('Guest with this ID already exists') ||
      text.includes('"id"')
    ) {
      return 'This guest or ID document already exists for this hotel. Please select the existing guest record or use a different ID number.';
    }
    return text || 'Failed to update guest information. Please try again.';
  };

  // ── Field change handlers ──────────────────────────────────────────────────
  const handleNameChange = (value) => {
    const { first_name, middle_name, last_name } = parseFullName(value);
    setFormData((prev) => ({ ...prev, name: value, first_name, middle_name, last_name }));
    if (touchedFields.name || touchedFields.first_name) {
      setValidationErrors((prev) => ({ ...prev, first_name: validateName(first_name || value) }));
    }
  };

  const handleEmailChange = (value) => {
    setFormData((prev) => ({ ...prev, email: value }));
    if (touchedFields.email) {
      setValidationErrors((prev) => ({ ...prev, email: validateEmail(value) }));
    } else {
      setValidationErrors((prev) => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    const dialCode = formData.phone_country_code || '+91';
    const digitsOnly = normalizePhoneInputForCountry(value, dialCode);
    setFormData((prev) => ({ ...prev, phone: digitsOnly }));
    if (touchedFields.phone) {
      setValidationErrors((prev) => ({ ...prev, phone: validatePhone(digitsOnly, dialCode) }));
    }
    if (phoneSearchDebounceTimerRef.current)
      clearTimeout(phoneSearchDebounceTimerRef.current);
    if (editingGuestIdProp) {
      const maxLen = getPhoneMaxLength(dialCode);
      if (digitsOnly.length === maxLen) {
        phoneSearchDebounceTimerRef.current = setTimeout(
          () => handleAutoSearchGuest(digitsOnly),
          400,
        );
      } else {
        setGuestSearchOptions([]);
      }
      return;
    }
    if (editingGuestId) {
      setEditingGuestId(null);
      setIsPhoneLocked(false);
    }
    const maxLen = getPhoneMaxLength(dialCode);
    if (digitsOnly.length === maxLen) {
      phoneSearchDebounceTimerRef.current = setTimeout(
        () => handleAutoSearchGuest(digitsOnly),
        400,
      );
    } else {
      setGuestSearchOptions([]);
    }
  };

  const handleFieldBlur = (fieldName) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    if (fieldName === 'phone') {
      setValidationErrors((prev) => ({
        ...prev,
        phone: validatePhone(formData.phone, formData.phone_country_code),
      }));
    } else if (fieldName === 'email') {
      setValidationErrors((prev) => ({ ...prev, email: validateEmail(formData.email) }));
    } else if (fieldName === 'name' || fieldName === 'first_name') {
      const name = fieldName === 'name' ? formData.name : formData.first_name || formData.name;
      setValidationErrors((prev) => ({ ...prev, first_name: validateName(name) }));
    } else if (fieldName === 'pincode' || fieldName === 'zipcode') {
      setValidationErrors((prev) => ({ ...prev, zipcode: validatePincode(formData.zipcode) }));
    } else if (fieldName === 'city') {
      setValidationErrors((prev) => ({
        ...prev,
        city: validateCity(formData.city, !!(formData.address?.trim())),
      }));
    } else if (fieldName === 'country') {
      setValidationErrors((prev) => ({ ...prev, country: validateCountry(formData.country) }));
    } else if (fieldName === 'address') {
      const hasAddr = !!(formData.address?.trim());
      if (touchedFields.city)
        setValidationErrors((prev) => ({
          ...prev,
          city: validateCity(formData.city, hasAddr),
        }));
      if (touchedFields.country)
        setValidationErrors((prev) => ({
          ...prev,
          country: validateCountry(formData.country),
        }));
    } else if (fieldName === 'id_number') {
      setValidationErrors((prev) => ({
        ...prev,
        id_number: validateDocumentIdNumber(effectiveDocumentType, formData.id_number),
      }));
    } else if (fieldName === 'document_type') {
      setValidationErrors((prev) => ({
        ...prev,
        id_number: validateDocumentIdNumber(effectiveDocumentType, formData.id_number),
      }));
    }
  };

  // ── Phone search / autofill ────────────────────────────────────────────────
  const handleAutoSearchGuest = async (phoneValue) => {
    const dialCode = formData.phone_country_code || '+91';
    const phone = String(phoneValue || '').trim().replace(/\D/g, '');
    if (!phone || phone.length !== getPhoneMaxLength(dialCode)) {
      setGuestSearchOptions([]);
      return;
    }
    setSearching(true);
    try {
      const response = await searchGuestByPhone(phone);
      if (response.guest) {
        let fullGuestData = response.guest;
        try {
          const det = await getCustomerDetail(response.guest.id);
          if (det?.customer) fullGuestData = { ...fullGuestData, ...det.customer };
        } catch {
          // use search result
        }
        const guestName = [
          fullGuestData.first_name || '',
          fullGuestData.middle_name || '',
          fullGuestData.last_name || '',
        ].filter(Boolean).join(' ') || fullGuestData.name || 'Guest';
        const optPhone = (phone || '').replace(/\D/g, '') || fullGuestData.phone;
        setGuestSearchOptions([
          {
            id: fullGuestData.id,
            name: guestName,
            phone: optPhone,
            email: fullGuestData.email,
            guest: fullGuestData,
          },
        ]);
      } else {
        setGuestSearchOptions([]);
      }
    } catch {
      setGuestSearchOptions([]);
    } finally {
      setSearching(false);
    }
  };

  const applySelectedGuestToForm = useCallback((option) => {
    if (!option?.guest) return;
    const g = option.guest;
    let phone = (option.phone || '').toString().replace(/\D/g, '') || '';
    let cc = '+91';
    if (g.phone) {
      const s = g.phone.toString();
      if (s.includes('-')) {
        const parts = s.split('-');
        if (parts.length >= 2) {
          cc = parts[0].startsWith('+') ? parts[0] : '+' + parts[0];
          if (!phone) phone = parts.slice(1).join('').replace(/\D/g, '');
        }
      } else if (s.startsWith('+')) {
        const m = s.match(/^(\+\d{1,3})(\d+)$/);
        if (m) { cc = m[1]; if (!phone) phone = m[2]; }
        else if (!phone) phone = s.replace(/\D/g, '');
      } else if (!phone) phone = s.replace(/\D/g, '');
    }
    if (g.country_code) cc = g.country_code.startsWith('+') ? g.country_code : '+' + g.country_code;
    const fullName = [g.first_name, g.middle_name, g.last_name].filter(Boolean).join(' ').trim();
    const dobRaw = g.dob || g.date_of_birth || '';
    const dob = dobRaw ? String(dobRaw).slice(0, 10) : '';
    const gender = String(g.gender || '').trim().toLowerCase();
    const toDocUrl = (raw) => {
      if (!raw) return '';
      const s = String(raw);
      if (s.startsWith('http') || s.startsWith('data:')) return s;
      return s.startsWith('/')
        ? `${window.location.origin}${s}`
        : `${window.location.origin}/${s}`;
    };
    const idFrontUrl = toDocUrl(g.id_front_url || g.id_front || g.document_front_url);
    const idBackUrl = toDocUrl(g.id_back_url || g.id_back || g.document_back_url);
    const countryObj = g.country
      ? countriesData.countries.find(
          (c) =>
            c.code === g.country
            || c.name === g.country
            || String(c.name).toLowerCase() === String(g.country).toLowerCase(),
        )
      : null;
    const resolvedCountry = countryObj ? countryObj.name : (g.country || '');
    const stateObj = countryObj?.states?.find(
      (s) =>
        s.name === g.state
        || s.code === g.state
        || String(s.name).toLowerCase() === String(g.state || '').toLowerCase(),
    );
    const stateName = stateObj ? stateObj.name : (g.state || '');
    const cityList = stateObj?.cities || [];
    const cityName = cityList.find(
      (c) => String(c).toLowerCase() === String(g.city || '').toLowerCase(),
    ) || (g.city || '');
    setFormData((prev) => ({
      ...prev,
      phone: phone || prev.phone,
      phone_country_code: cc,
      title: g.title || '',
      name: g.name || fullName || '',
      first_name: g.first_name || '',
      middle_name: g.middle_name || '',
      last_name: g.last_name || '',
      email: g.email || '',
      dob,
      gender,
      address: g.address_line1 || g.address || '',
      zipcode: g.pincode || g.zipcode || '',
      country: resolvedCountry,
      state: stateName,
      city: cityName,
      document_type: String(g.document_type || '').trim() || NO_DOCUMENT_VALUE,
      id_number: g.id_number || '',
      id_front: idFrontUrl || '',
      id_back: idBackUrl || '',
      do_not_rent: Boolean(g.do_not_rent),
      do_not_rent_severity: g.do_not_rent_severity || 'HIGH',
      do_not_rent_reason: g.do_not_rent_reason || '',
    }));
    setForeignGuestDetails(mapGuestApiToForeignDetails(g, resolvedCountry));
    setForeignGuestDrawerOpen(false);
    setEditingGuestId(g.id || null);
    setIsPhoneLocked(Boolean(g.id));
    if (stateName || cityName) {
      setTimeout(() => {
        setFormData((prev) => ({
          ...prev,
          state: stateName || prev.state,
          city: cityName || prev.city,
        }));
      }, 150);
    }
    setGuestSearchOptions([]);
    setValidationErrors((prev) => ({
      ...prev,
      name: '',
      phone: '',
      id_number: '',
      ...(dob ? { dob: '' } : {}),
      ...(gender ? { gender: '' } : {}),
    }));
    setTouchedFields((prev) => ({
      ...prev,
      name: true,
      phone: true,
      id_number: true,
      document_type: true,
      ...(dob ? { dob: true } : {}),
      ...(gender ? { gender: true } : {}),
    }));
  }, []);

  // ── AI Add Guest callback ──────────────────────────────────────────────────
  const handleAiAddGuestSuccess = useCallback((guest, previews) => {
    const g = guest || {};
    const p = typeof previews === 'string'
      ? { frontDataUrl: previews, backDataUrl: null }
      : previews || {};
    const firstName = String(g.guest_first_name || '').trim();
    const middleName = String(g.guest_middle_name || '').trim();
    const lastName = String(g.guest_last_name || '').trim();
    const fullName =
      [firstName, middleName, lastName].filter(Boolean).join(' ') ||
      String(g.guest_name || g.name || '').trim();
    const docType =
      String(g.upload_document_type || g.document_type || 'aadhaar').trim() || 'aadhaar';
    const dobRaw = g.guest_dob || g.dob || g.date_of_birth || '';
    const dob = dobRaw ? String(dobRaw).trim().slice(0, 10) : '';
    const gender = String(g.guest_gender || g.gender || '').trim().toLowerCase();
    setFormData((prev) => ({
      ...prev,
      title: String(g.guest_title || prev.title || '').trim(),
      name: fullName || prev.name,
      first_name: firstName || prev.first_name,
      middle_name: middleName || prev.middle_name,
      last_name: lastName || prev.last_name,
      email: String(g.guest_email || prev.email || '').trim(),
      phone: prev.phone,
      dob: dob || prev.dob,
      gender: gender || prev.gender,
      address: String(g.guest_address || prev.address || '').trim(),
      city: String(g.guest_city || prev.city || '').trim(),
      state: String(g.guest_state || prev.state || '').trim(),
      country: String(g.guest_country || g.nationality || prev.country || '').trim(),
      zipcode: String(g.guest_pincode || prev.zipcode || '').trim(),
      document_type: docType,
      id_number:
        g.document_id_number != null
          ? normalizeDocumentIdInput(docType, g.document_id_number)
          : prev.id_number,
      id_front: p.frontDataUrl || prev.id_front,
      id_back: p.backDataUrl || prev.id_back,
    }));
    setTouchedFields((prev) => ({
      ...prev, name: true, id_number: true, document_type: true,
    }));
    setValidationErrors((prev) => ({ ...prev, name: '', id_number: '' }));
    setIsPhoneLocked(false);
    setGuestSearchOptions([]);
    setAiAddGuestOpen(false);
    applyGuestCountryChange(String(g.guest_country || g.nationality || '').trim());
  }, [applyGuestCountryChange]);

  // ── File / Camera ──────────────────────────────────────────────────────────
  const showCameraPermissionHint = useCallback((message) => {
    setCameraPermissionHint({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings.',
    });
  }, []);

  const clearDocumentImageError = () => {
    setValidationErrors((prev) =>
      prev.id_front || prev.id_back ? { ...prev, id_front: '', id_back: '' } : prev,
    );
    setTouchedFields((prev) => ({ ...prev, id_front: true, id_back: true }));
  };

  const syncDocumentImageErrors = (idFront, idBack) => {
    const imageErrors = validateGuestDocumentImages(formData.document_type, idFront, idBack);
    setTouchedFields((prev) => ({ ...prev, id_front: true, id_back: true }));
    setValidationErrors((prev) => ({
      ...prev,
      id_front: imageErrors.id_front,
      id_back: imageErrors.id_back,
    }));
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === 'front') setFormData((prev) => ({ ...prev, id_front: reader.result }));
      else setFormData((prev) => ({ ...prev, id_back: reader.result }));
      clearDocumentImageError();
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async (type) => {
    try {
      const native = await captureNativeCameraDataUrl();
      if (native) {
        if (type === 'front') setFormData((prev) => ({ ...prev, id_front: native }));
        else setFormData((prev) => ({ ...prev, id_back: native }));
        clearDocumentImageError();
        return;
      }
    } catch (err) {
      const msg = getNativeCameraErrorMessage(err);
      if (msg) showCameraPermissionHint(msg);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionHint(
        'Camera is not available. Turn off Camera upload to upload ID images from files.',
      );
      return;
    }
    try {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      const ms = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setStream(ms);
      setCameraType(type);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = ms;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch {
      showCameraPermissionHint(
        'Camera access was denied or is unavailable. Allow camera permission for this site, or turn off Camera upload to choose a file.',
      );
    }
  };

  const stopCamera = () => {
    if (stream) { stream.getTracks().forEach((t) => t.stop()); setStream(null); }
    setCameraOpen(false);
    setCameraType(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    ctx.drawImage(v, 0, 0);
    const data = c.toDataURL('image/jpeg', 0.8);
    if (cameraType === 'front') setFormData((prev) => ({ ...prev, id_front: data }));
    else setFormData((prev) => ({ ...prev, id_back: data }));
    clearDocumentImageError();
    stopCamera();
  };

  const handleUploadClick = (type) => {
    if (formData.camera_upload) {
      startCamera(type);
    } else {
      if (type === 'front') fileInputFrontRef.current?.click();
      else fileInputBackRef.current?.click();
    }
  };

  // ── Save handler ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!bookingId && !deferGuestPersist) return;

    setSaving(true);
    setGuestFormError(null);

    try {
      // Parse phone
      let phoneNumber = null;
      let countryCode = null;
      if (formData.phone?.trim()) {
        phoneNumber = formData.phone.trim().replace(/\D/g, '');
        countryCode = formData.phone_country_code || '+91';
        if (!countryCode.startsWith('+')) countryCode = '+' + countryCode;
      }

      // Parse name
      let firstName = formData.first_name;
      let middleName = formData.middle_name;
      let lastName = formData.last_name;
      if (formData.name?.trim()) {
        const parsed = parseFullName(formData.name);
        firstName = parsed.first_name;
        middleName = parsed.middle_name;
        lastName = parsed.last_name;
      }

      // Validate
      const errors = {};
      const nameErr = validateName(firstName || formData.name);
      if (nameErr) {
        errors.first_name = nameErr;
        setTouchedFields((prev) => ({ ...prev, name: true, first_name: true }));
      }
      const phoneErr = validatePhone(formData.phone, countryCode || formData.phone_country_code);
      if (phoneErr) {
        errors.phone = phoneErr;
        setTouchedFields((prev) => ({ ...prev, phone: true }));
      }
      if (formData.email?.trim()) {
        const emailErr = validateEmail(formData.email);
        if (emailErr) { errors.email = emailErr; setTouchedFields((p) => ({ ...p, email: true })); }
      }
      if (formData.zipcode?.trim()) {
        const pinErr = validatePincode(formData.zipcode);
        if (pinErr) { errors.zipcode = pinErr; setTouchedFields((p) => ({ ...p, zipcode: true })); }
      }
      const countryErr = validateCountry(formData.country);
      if (countryErr) { errors.country = countryErr; setTouchedFields((p) => ({ ...p, country: true })); }
      if (!formData.dob?.trim()) {
        errors.dob = 'Date of birth is required.';
        setTouchedFields((p) => ({ ...p, dob: true }));
      } else if (moment(formData.dob).isAfter(moment(), 'day')) {
        errors.dob = 'Date of birth cannot be in the future.';
        setTouchedFields((p) => ({ ...p, dob: true }));
      }
      if (!formData.gender?.trim()) {
        errors.gender = 'Gender is required.';
        setTouchedFields((p) => ({ ...p, gender: true }));
      }
      if (isForeignGuest && !foreignGuestComplete) {
        errors.foreignGuest = 'Foreign guest details are required';
        setForeignGuestDrawerOpen(true);
      }
      if (formData.document_type !== NO_DOCUMENT_VALUE) {
        const idErr = validateDocumentIdNumber(effectiveDocumentType, formData.id_number);
        if (idErr) {
          errors.id_number = idErr;
          setTouchedFields((p) => ({ ...p, id_number: true, document_type: true }));
        }
        const imageErrors = validateGuestDocumentImages(
          formData.document_type,
          formData.id_front,
          formData.id_back,
        );
        if (imageErrors.id_front) {
          errors.id_front = imageErrors.id_front;
          setTouchedFields((p) => ({ ...p, id_front: true }));
        }
        if (imageErrors.id_back) {
          errors.id_back = imageErrors.id_back;
          setTouchedFields((p) => ({ ...p, id_back: true }));
        }
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setGuestFormError('Please fix the validation errors before saving.');
        setSaving(false);
        return;
      }
      if (!firstName?.trim()) {
        setGuestFormError('Name is required.');
        setSaving(false);
        return;
      }
      let resolvedGuestId = editingGuestId;
      if (!resolvedGuestId && guestSearchOptions.length > 0) {
        const digits = String(phoneNumber || '').replace(/\D/g, '');
        const matched =
          guestSearchOptions.find(
            (opt) => String(opt.phone || '').replace(/\D/g, '') === digits,
          ) || guestSearchOptions[0];
        if (matched?.id) {
          resolvedGuestId = matched.id;
          setEditingGuestId(matched.id);
        }
      }

      // ── Deferred mode (compact check-in room selection) ──────────────────
      if (deferGuestPersist) {
        const staged = {
          ...formData,
          first_name: firstName || '',
          middle_name: middleName || '',
          last_name: lastName || '',
          name:
            (formData.name || '').trim() ||
            [firstName, middleName, lastName].filter(Boolean).join(' '),
        };
        onDeferredSave?.(staged);
        onClose();
        return;
      }

      // ── Build API payload ────────────────────────────────────────────────
      const guestData = {};
      if (resolvedGuestId) guestData.id = resolvedGuestId;
      if (editingGuestIdProp) guestData.replace_guest_id = editingGuestIdProp;
      if (firstName) guestData.first_name = firstName.trim();
      if (middleName) guestData.middle_name = middleName.trim();
      if (lastName) guestData.last_name = lastName.trim();
      if (formData.email?.trim()) guestData.email = formData.email.trim();
      if (resolvedGuestId) {
        guestData.phone = phoneNumber ? phoneNumber.trim() : '';
        guestData.country_code = (countryCode || formData.phone_country_code || '+91').trim();
      } else {
        if (phoneNumber) guestData.phone = phoneNumber.trim();
        if (countryCode) guestData.country_code = countryCode.trim();
      }
      if (formData.title?.trim()) guestData.title = formData.title.trim();
      if (formData.address?.trim()) guestData.address = formData.address.trim();
      if (formData.city?.trim()) guestData.city = formData.city.trim();
      if (formData.state?.trim()) guestData.state = formData.state.trim();
      if (formData.country?.trim()) guestData.country = formData.country.trim();
      if (formData.zipcode?.trim()) guestData.pincode = formData.zipcode.trim();
      guestData.dob = formData.dob ? String(formData.dob).slice(0, 10) : '';
      if (formData.gender?.trim()) guestData.gender = formData.gender.trim();
      if (formData.document_type === NO_DOCUMENT_VALUE) {
        guestData.document_type = '';
      } else if (effectiveDocumentType) {
        guestData.document_type = effectiveDocumentType;
      }
      if (effectiveDocumentType && formData.id_number?.trim()) {
        guestData.id_number = normalizeDocumentIdForSubmit(effectiveDocumentType, formData.id_number);
      }
      if (typeof formData.id_front === 'string' && formData.id_front.startsWith('data:image')) {
        guestData.id_front = formData.id_front;
      }
      if (typeof formData.id_back === 'string' && formData.id_back.startsWith('data:image')) {
        guestData.id_back = formData.id_back;
      }
      if (isForeignGuest) {
        Object.assign(guestData, foreignDetailsToGuestApiPayload(foreignGuestDetails));
      }

      // ── Resolve items to update ──────────────────────────────────────────
      let itemsToUpdate = [];
      if (resolveItemsToUpdate) {
        itemsToUpdate = await resolveItemsToUpdate();
      } else if (bookingItemIds?.length) {
        itemsToUpdate = bookingItemIds.map((id) => ({ id }));
      }
      if (!itemsToUpdate.length) {
        setGuestFormError('No booking items found.');
        setSaving(false);
        return;
      }

      // ── PATCH primary ────────────────────────────────────────────────────
      const primary = itemsToUpdate[0];
      const response = await api.patch(`/booking/api/${bookingId}/`, {
        booking_item_id: primary.id,
        guest: guestData,
      });
      if (!response.data.success) {
        throw new Error(
          response.data.error || response.data.message || 'Failed to update guest information.',
        );
      }

      // ── PATCH remaining items (best-effort) ──────────────────────────────
      for (const item of itemsToUpdate.slice(1)) {
        try {
          await api.patch(`/booking/api/${bookingId}/`, {
            booking_item_id: item.id,
            guest: guestData,
          });
        } catch {
          // silently skip secondary failures
        }
      }

      onSuccessMessage?.('Guest information updated successfully.');
      await onSaved?.();
      onClose();
    } catch (err) {
      const ed = err.response?.data || {};
      let msg = 'Failed to update guest information.';
      if (ed.error) msg = typeof ed.error === 'string' ? ed.error : JSON.stringify(ed.error);
      else if (ed.message) msg = ed.message;
      else if (ed.detail) msg = ed.detail;
      else if (err.message) msg = err.message;
      setGuestFormError(formatGuestSaveError(msg));
    } finally {
      setSaving(false);
    }
  };

  // ── JSX ────────────────────────────────────────────────────────────────────
  const inputSx = (errorKey) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& fieldset': {
        borderColor:
          validationErrors[errorKey] && touchedFields[errorKey]
            ? theme.palette.error.main
            : isDark
            ? drawerTheme.surfaceBorder
            : 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover fieldset': {
        borderColor:
          validationErrors[errorKey] && touchedFields[errorKey]
            ? theme.palette.error.main
            : CUSTOM_BORDER_COLOR,
      },
      '&.Mui-focused fieldset': {
        borderColor:
          validationErrors[errorKey] && touchedFields[errorKey]
            ? theme.palette.error.main
            : CUSTOM_BORDER_COLOR,
      },
    },
    '& .MuiInputBase-input': {
      backgroundColor: 'transparent !important',
      color: isDark ? drawerTheme.text : '#333',
    },
    '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
  });
  const plainInputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& fieldset': { borderColor: isDark ? '#444' : '#e0e0e0' },
      '&:hover fieldset': { borderColor: CUSTOM_BORDER_COLOR },
      '&.Mui-focused fieldset': { borderColor: CUSTOM_BORDER_COLOR },
    },
    '& .MuiInputBase-input': {
      backgroundColor: 'transparent !important',
      color: isDark ? drawerTheme.text : '#333',
    },
    '& .MuiInputLabel-root': { color: isDark ? drawerTheme.subtleText : '#666' },
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => !saving && onClose()}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, { width: { xs: '100%', sm: 600 } }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              ...getBookingDrawerHeaderSx(isDark),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAddIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                Guest Information
              </Typography>
              {roomLabel && (
                <>
                  <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', mx: 0.5 }}>
                    -
                  </Typography>
                  <Chip label={roomLabel} size="small" sx={getGuestDrawerMetaChipSx(isDark)} />
                </>
              )}
              {booking && !roomLabel && (
                <>
                  <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', mx: 0.5 }}>
                    -
                  </Typography>
                  <BookingDrawerCodeChip
                    booking={booking}
                    bookingId={bookingId}
                    label={booking.code}
                    sx={getGuestDrawerMetaChipSx(isDark)}
                  />
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {canManageAiCheckin && (
                <AiGradientButton
                  size="small"
                  onClick={() => setAiAddGuestOpen(true)}
                  sx={{
                    minHeight: 30,
                    px: { xs: 0.85, sm: 1.15 },
                    borderRadius: 999,
                    fontSize: { xs: '0.72rem', sm: '0.78rem' },
                    fontWeight: 750,
                    whiteSpace: 'nowrap',
                    '& .MuiButton-startIcon': { mr: 0.5, '& svg': { fontSize: 16 } },
                  }}
                >
                  AI Add Guest
                </AiGradientButton>
              )}
              <IconButton
                onClick={() => !saving && onClose()}
                size="small"
                sx={{ color: isDark ? drawerTheme.subtleText : '#666', p: 0.5 }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 1.5,
              ...getGuestDrawerContentSx(isDark),
            }}
          >
            <Grid container spacing={2}>
              {/* Phone with country code */}
              <Grid item xs={12} md={6}>
                <Box ref={phoneInputAnchorRef}>
                  <TextField
                    fullWidth
                    name="mobile"
                    label="Mobile Number"
                    required
                    value={formData.phone ?? ''}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    error={!!validationErrors.phone && touchedFields.phone}
                    helperText={
                      (touchedFields.phone && validationErrors.phone) ||
                      `Enter ${formatPhoneLengthLabel(formData.phone_country_code || '+91')}; if a saved guest appears, select them to load details`
                    }
                    onBlur={() => handleFieldBlur('phone')}
                    inputProps={{
                      maxLength: getPhoneMaxLength(formData.phone_country_code || '+91'),
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            ref={phoneCountryCodeAnchorRef}
                            onClick={() => setPhoneCountryCodeMenuOpen(true)}
                            sx={{
                              display: 'flex', alignItems: 'center', cursor: 'pointer',
                              gap: 0.5, px: 1, py: 0.5, borderRadius: 1,
                              '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: '1.2rem' }}>
                              {selectedPhoneCountryCode?.flag || '🇮🇳'}
                            </Typography>
                            <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                              {selectedPhoneCountryCode?.dialCode || '+91'}
                            </Typography>
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>▼</Typography>
                          </Box>
                        </InputAdornment>
                      ),
                      endAdornment: searching
                        ? <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment>
                        : null,
                    }}
                    sx={inputSx('phone')}
                  />
                </Box>
                {/* Guest search dropdown */}
                <Popper
                  open={guestSearchOptions.length > 0}
                  anchorEl={phoneInputAnchorRef.current}
                  placement="bottom-start"
                  style={{ zIndex: theme.zIndex.modal + 1 }}
                  modifiers={[{ name: 'flip', enabled: false }]}
                >
                  <Paper elevation={8} sx={{ width: phoneInputAnchorRef.current?.offsetWidth || 320, maxHeight: 220, overflow: 'auto', mt: 0.5 }}>
                    {guestSearchOptions.map((opt) => (
                      <Box
                        key={opt.id || opt.phone}
                        component="div"
                        onClick={() => applySelectedGuestToForm(opt)}
                        sx={{
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                          py: 1.5, px: 2, cursor: 'pointer',
                          '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
                          borderBottom: '1px solid', borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? drawerTheme.text : '#333' }}>
                          {opt.name || 'Guest'}
                        </Typography>
                        {opt.phone && <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>{opt.phone}</Typography>}
                        {opt.email && <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>{opt.email}</Typography>}
                      </Box>
                    ))}
                  </Paper>
                </Popper>
                {/* Country code picker */}
                <Menu
                  anchorEl={phoneCountryCodeAnchorRef.current}
                  open={phoneCountryCodeMenuOpen}
                  onClose={() => { setPhoneCountryCodeMenuOpen(false); setPhoneCountryCodeSearch(''); }}
                  PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
                >
                  <Box sx={{ p: 1 }}>
                    <TextField
                      fullWidth size="small" placeholder="Search country..."
                      value={phoneCountryCodeSearch}
                      onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  {countryCodes
                    .filter((c) => {
                      if (!phoneCountryCodeSearch) return true;
                      const s = phoneCountryCodeSearch.toLowerCase();
                      return (
                        c.name.toLowerCase().includes(s) ||
                        c.dialCode.includes(s) ||
                        c.code.toLowerCase().includes(s)
                      );
                    })
                    .map((c) => (
                      <MenuItem
                        key={c.code}
                        selected={selectedPhoneCountryCode?.dialCode === c.dialCode}
                        onClick={() => {
                          const trimmed = normalizePhoneInputForCountry(formData.phone, c.dialCode);
                          setFormData((prev) => ({
                            ...prev,
                            phone_country_code: c.dialCode,
                            phone: trimmed,
                          }));
                          if (touchedFields.phone) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              phone: validatePhone(trimmed, c.dialCode),
                            }));
                          }
                          setPhoneCountryCodeMenuOpen(false);
                          setPhoneCountryCodeSearch('');
                        }}
                      >
                        <ListItemIcon>
                          <Typography sx={{ fontSize: '1.2rem' }}>{c.flag}</Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={c.dialCode}
                          secondary={c.name}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </MenuItem>
                    ))}
                </Menu>
              </Grid>

              {/* Name with title picker */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name || ''}
                  onChange={(e) => handleNameChange(e.target.value)}
                  onBlur={() => handleFieldBlur('name')}
                  required
                  error={!!validationErrors.first_name && (touchedFields.name || touchedFields.first_name)}
                  helperText={
                    (touchedFields.name || touchedFields.first_name) ? validationErrors.first_name : ''
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          ref={titleMenuAnchorRef}
                          onClick={() => setTitleMenuOpen(true)}
                          sx={{
                            display: 'flex', alignItems: 'center', cursor: 'pointer',
                            gap: 0.5, px: 1, py: 0.5, borderRadius: 1,
                            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' },
                          }}
                        >
                          <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem', minWidth: 40 }}>
                            {formData.title || 'Title'}
                          </Typography>
                          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>▼</Typography>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx('first_name')}
                />
                <Menu
                  anchorEl={titleMenuAnchorRef.current}
                  open={titleMenuOpen}
                  onClose={() => setTitleMenuOpen(false)}
                  PaperProps={{ sx: { maxHeight: 300, minWidth: 150 } }}
                >
                  <MenuItem onClick={() => { setFormData((p) => ({ ...p, title: '' })); setTitleMenuOpen(false); }}>
                    <em>None</em>
                  </MenuItem>
                  {titleOptions.map((t) => (
                    <MenuItem
                      key={t.value}
                      selected={formData.title === t.value}
                      onClick={() => { setFormData((p) => ({ ...p, title: t.value })); setTitleMenuOpen(false); }}
                    >
                      {t.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Grid>

              {/* DOB */}
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <HotelDatePicker
                    label="Date of Birth"
                    value={formData.dob ? moment(formData.dob) : null}
                    onChange={(date) => {
                      setTouchedFields((p) => ({ ...p, dob: true }));
                      if (date?.isValid()) {
                        if (date.isAfter(moment(), 'day')) {
                          setValidationErrors((p) => ({ ...p, dob: 'Date of birth cannot be in the future' }));
                          return;
                        }
                        setFormData((p) => ({ ...p, dob: date.format('YYYY-MM-DD') }));
                        setValidationErrors((p) => { const n = { ...p }; delete n.dob; return n; });
                      } else {
                        setFormData((p) => ({ ...p, dob: '' }));
                        setValidationErrors((p) => ({ ...p, dob: 'Date of birth is required.' }));
                      }
                    }}
                    maxDate={moment()}
                    slotProps={{
                      ...datePickerSlotProps,
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!validationErrors.dob && touchedFields.dob,
                        helperText:
                          (touchedFields.dob && validationErrors.dob) ||
                          (isGuestChild(formData.dob) ? 'Under 18 — counted as child' : ''),
                        sx: {
                          ...inputSx('dob'),
                          '& .MuiFormHelperText-root': {
                            color:
                              isGuestChild(formData.dob) && !validationErrors.dob
                                ? isDark ? '#90caf9' : '#1565c0'
                                : undefined,
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Gender */}
              <Grid item xs={12} md={6}>
                <FormControl
                  fullWidth
                  required
                  error={!!validationErrors.gender && touchedFields.gender}
                >
                  <InputLabel sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                    Gender
                  </InputLabel>
                  <Select
                    label="Gender"
                    value={formData.gender || ''}
                    onChange={(e) => {
                      const v = e.target.value || '';
                      setFormData((p) => ({ ...p, gender: v }));
                      setTouchedFields((p) => ({ ...p, gender: true }));
                      setValidationErrors((p) => {
                        const n = { ...p };
                        if (v) delete n.gender; else n.gender = 'Gender is required.';
                        return n;
                      });
                    }}
                    onBlur={() => setTouchedFields((p) => ({ ...p, gender: true }))}
                    MenuProps={selectMenuProps}
                    sx={{
                      backgroundColor: 'transparent',
                      color: isDark ? drawerTheme.text : '#333',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor:
                          validationErrors.gender && touchedFields.gender
                            ? theme.palette.error.main
                            : isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor:
                          validationErrors.gender && touchedFields.gender
                            ? theme.palette.error.main
                            : CUSTOM_BORDER_COLOR,
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor:
                          validationErrors.gender && touchedFields.gender
                            ? theme.palette.error.main
                            : CUSTOM_BORDER_COLOR,
                      },
                    }}
                  >
                    <MenuItem value=""><em>Select Gender</em></MenuItem>
                    {GENDER_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {touchedFields.gender && validationErrors.gender && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {validationErrors.gender}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Guest Address"
                  value={formData.address || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((p) => ({ ...p, address: v }));
                    const hasAddr = !!(v?.trim());
                    if (touchedFields.city)
                      setValidationErrors((p) => ({ ...p, city: validateCity(formData.city, hasAddr) }));
                    if (touchedFields.country)
                      setValidationErrors((p) => ({ ...p, country: validateCountry(formData.country) }));
                  }}
                  onBlur={() => handleFieldBlur('address')}
                  multiline
                  rows={2}
                  sx={plainInputSx}
                />
              </Grid>

              {/* Country / State / City */}
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(o) => o.name}
                  filterOptions={countryFilterOptions}
                  value={countries.find((c) => c.name === formData.country) || null}
                  onChange={(_, nv) => {
                    const v = nv ? nv.name : '';
                    setFormData((p) => ({ ...p, country: v, state: '', city: '' }));
                    setTouchedFields((p) => ({ ...p, country: true }));
                    applyGuestCountryChange(v);
                  }}
                  onBlur={() => handleFieldBlur('country')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      error={
                        (!!validationErrors.country && touchedFields.country)
                        || !!validationErrors.foreignGuest
                      }
                      helperText={
                        validationErrors.foreignGuest
                        || (touchedFields.country && validationErrors.country)
                        || (isForeignGuest
                          ? 'Foreign guest details required'
                          : (formData.country ? 'Auto-selected from hotel' : ''))
                      }
                      sx={plainInputSx}
                    />
                  )}
                />
                {isForeignGuest ? (
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      icon={<PublicIcon sx={{ fontSize: '16px !important' }} />}
                      label={
                        foreignGuestComplete
                          ? 'Foreign guest details saved'
                          : 'Add foreign guest details'
                      }
                      onClick={() => setForeignGuestDrawerOpen(true)}
                      color={foreignGuestComplete ? 'success' : 'warning'}
                      variant={foreignGuestComplete ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 700 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setForeignGuestDrawerOpen(true)}
                      aria-label="Edit foreign guest details"
                      sx={{ color: CUSTOM_BORDER_COLOR }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                ) : null}
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={availableStates}
                  getOptionLabel={(o) => o.name}
                  filterOptions={stateFilterOptions}
                  value={availableStates.find((s) => s.name === formData.state) || null}
                  onChange={(_, nv) => {
                    setFormData((p) => ({ ...p, state: nv ? nv.name : '', city: '' }));
                  }}
                  disabled={!selectedCountryObj}
                  renderInput={(params) => <TextField {...params} label="State" sx={plainInputSx} />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={availableCities}
                  getOptionLabel={(o) => o}
                  filterOptions={cityFilterOptions}
                  value={availableCities.find((c) => c === formData.city) || null}
                  onChange={(_, nv) => {
                    const v = nv || '';
                    setFormData((p) => ({ ...p, city: v }));
                    if (touchedFields.city || !!(formData.address?.trim())) {
                      setValidationErrors((p) => ({
                        ...p,
                        city: validateCity(v, !!(formData.address?.trim())),
                      }));
                      setTouchedFields((p) => ({ ...p, city: true }));
                    }
                  }}
                  onBlur={() => handleFieldBlur('city')}
                  disabled={!selectedCountryObj || !formData.state}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      error={!!validationErrors.city && touchedFields.city}
                      helperText={touchedFields.city ? validationErrors.city : ''}
                      sx={plainInputSx}
                    />
                  )}
                />
              </Grid>

              {/* Email / Pincode */}
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={() => handleFieldBlur('email')}
                  error={!!validationErrors.email && touchedFields.email}
                  helperText={touchedFields.email ? validationErrors.email : ''}
                  inputProps={{ autoComplete: 'email' }}
                  sx={inputSx('email')}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  value={formData.zipcode || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData((p) => ({ ...p, zipcode: v }));
                    if (touchedFields.zipcode)
                      setValidationErrors((p) => ({ ...p, zipcode: validatePincode(v) }));
                  }}
                  onBlur={() => handleFieldBlur('zipcode')}
                  error={!!validationErrors.zipcode && touchedFields.zipcode}
                  helperText={touchedFields.zipcode ? validationErrors.zipcode : ''}
                  sx={plainInputSx}
                />
              </Grid>

              {/* Do not rent */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: formData.do_not_rent ? 1 : 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.do_not_rent}
                        onChange={(e) => setFormData((p) => ({ ...p, do_not_rent: e.target.checked }))}
                        color="primary"
                      />
                    }
                    label="Do not Rent"
                    sx={{ color: isDark ? drawerTheme.text : '#333', m: 0 }}
                  />
                  {formData.do_not_rent && (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Severity</InputLabel>
                      <Select
                        value={formData.do_not_rent_severity}
                        label="Severity"
                        MenuProps={selectMenuProps}
                        onChange={(e) => setFormData((p) => ({ ...p, do_not_rent_severity: e.target.value }))}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#444' : '#e0e0e0' },
                          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: CUSTOM_BORDER_COLOR },
                        }}
                      >
                        {severityOptions.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                {formData.do_not_rent && (
                  <TextField
                    fullWidth
                    label="Reason for do not rent"
                    value={formData.do_not_rent_reason}
                    onChange={(e) => setFormData((p) => ({ ...p, do_not_rent_reason: e.target.value }))}
                    multiline
                    rows={2}
                    sx={plainInputSx}
                  />
                )}
              </Grid>

              {/* Document upload */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={isForeignGuest}>
                      <InputLabel>Select Document type</InputLabel>
                      <Select
                        value={formData.document_type}
                        label="Select Document type"
                        disabled={isForeignGuest}
                        MenuProps={selectMenuProps}
                        onChange={(e) => {
                          if (isForeignGuest) return;
                          const next = e.target.value;
                          setFormData((p) => ({
                            ...p,
                            document_type: next,
                            id_number: next === NO_DOCUMENT_VALUE ? '' : p.id_number,
                            id_front: next === NO_DOCUMENT_VALUE ? '' : p.id_front,
                            id_back: next === NO_DOCUMENT_VALUE ? '' : p.id_back,
                          }));
                          if (next === NO_DOCUMENT_VALUE) {
                            setValidationErrors((p) => ({ ...p, id_number: '' }));
                            return;
                          }
                          if (touchedFields.id_number || touchedFields.document_type) {
                            setValidationErrors((p) => ({
                              ...p,
                              id_number: validateDocumentIdNumber(next, formData.id_number),
                            }));
                          }
                        }}
                        onBlur={() => handleFieldBlur('document_type')}
                        sx={{
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor:
                              validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type)
                                ? theme.palette.error.main
                                : isDark ? drawerTheme.surfaceBorder : 'rgba(0, 0, 0, 0.23)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor:
                              validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type)
                                ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor:
                              validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type)
                                ? theme.palette.error.main : CUSTOM_BORDER_COLOR,
                          },
                        }}
                      >
                        {documentUploadOptions.map((d) => (
                          <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                        ))}
                      </Select>
                      {isForeignGuest ? (
                        <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, color: 'text.secondary' }}>
                          Set from Foreign Guest Details
                        </Typography>
                      ) : null}
                    </FormControl>
                  </Grid>
                  {!isNoDocumentSelected && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={`${getDocumentTypeLabel()} Number`}
                        value={normalizedIdNumberValue}
                        disabled={isForeignGuest}
                        onChange={(e) => {
                          if (isForeignGuest) return;
                          const v = normalizeDocumentIdInput(effectiveDocumentType, e.target.value);
                          setFormData((p) => ({ ...p, id_number: v }));
                          if (touchedFields.id_number)
                            setValidationErrors((p) => ({
                              ...p,
                              id_number: validateDocumentIdNumber(effectiveDocumentType, v),
                            }));
                        }}
                        onBlur={() => handleFieldBlur('id_number')}
                        placeholder={getDocumentNumberPlaceholder()}
                        required
                        error={!!validationErrors.id_number && (touchedFields.id_number || touchedFields.document_type)}
                        helperText={(touchedFields.id_number || touchedFields.document_type) ? validationErrors.id_number : ''}
                        inputProps={{
                          maxLength: isAadhaarDocumentSelected ? 14 : undefined,
                          inputMode: isAadhaarDocumentSelected ? 'numeric' : undefined,
                          pattern: isAadhaarDocumentSelected ? '[0-9\\-]*' : undefined,
                        }}
                        sx={inputSx('id_number')}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid container justifyContent="flex-end">
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center', height: '100%' }}>
                          <FormControlLabel
                            label="Camera upload"
                            labelPlacement="start"
                            control={
                              <Switch
                                checked={formData.camera_upload}
                                onChange={(e) => setFormData((p) => ({ ...p, camera_upload: e.target.checked }))}
                                color="primary"
                              />
                            }
                            sx={{ color: isDark ? drawerTheme.text : '#333' }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* ID Front */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                  {getFrontDocumentLabel()}
                  {!isNoDocumentSelected && !formData.id_front && !formData.id_back ? ' *' : ''}
                </Typography>
                <Paper
                  sx={getGuestDrawerUploadPaperSx(isDark, {
                    ...(validationErrors.id_front && touchedFields.id_front
                      ? {
                          borderColor: theme.palette.error.main,
                          backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)',
                          },
                        }
                      : {}),
                  })}
                  onClick={() => handleUploadClick('front')}
                >
                  {formData.id_front ? (
                    <Box>
                      <img src={formData.id_front} alt="ID Front" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }} />
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const nextFront = '';
                          const nextBack = formData.id_back;
                          setFormData((p) => ({ ...p, id_front: nextFront }));
                          syncDocumentImageErrors(nextFront, nextBack);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      {formData.camera_upload
                        ? <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        : <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />}
                      <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                        {formData.camera_upload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                      </Typography>
                    </Box>
                  )}
                  <input ref={fileInputFrontRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileUpload(e, 'front')} />
                </Paper>
                {validationErrors.id_front && touchedFields.id_front && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                    {validationErrors.id_front}
                  </Typography>
                )}
              </Grid>

              {/* ID Back */}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}>
                  {getBackDocumentLabel()}
                  {!isNoDocumentSelected && !formData.id_front && !formData.id_back ? ' *' : ''}
                </Typography>
                <Paper
                  sx={getGuestDrawerUploadPaperSx(isDark, {
                    ...(validationErrors.id_back && touchedFields.id_back
                      ? {
                          borderColor: theme.palette.error.main,
                          backgroundColor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.12)' : 'rgba(244, 67, 54, 0.06)',
                          },
                        }
                      : {}),
                  })}
                  onClick={() => handleUploadClick('back')}
                >
                  {formData.id_back ? (
                    <Box>
                      <img src={formData.id_back} alt="ID Back" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }} />
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          const nextFront = formData.id_front;
                          const nextBack = '';
                          setFormData((p) => ({ ...p, id_back: nextBack }));
                          syncDocumentImageErrors(nextFront, nextBack);
                        }}
                        sx={{ mt: 1 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      {formData.camera_upload
                        ? <CameraAltIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />
                        : <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }} />}
                      <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                        {formData.camera_upload ? 'Click to capture from camera' : 'Click to upload or drag and drop'}
                      </Typography>
                    </Box>
                  )}
                  <input ref={fileInputBackRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileUpload(e, 'back')} />
                </Paper>
                {validationErrors.id_back && touchedFields.id_back && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                    {validationErrors.id_back}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, ...getBookingDrawerFooterSx(isDark) }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => !saving && onClose()}
              disabled={saving}
              sx={getBookingDrawerOutlinedButtonSx(isDark)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSave}
              disabled={saving || !formData.name?.trim()}
              sx={getBookingDrawerPrimaryButtonSx(isDark)}
            >
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Box>

          {/* AI Add Guest overlay */}
          {canManageAiCheckin && (
            <WalkInAiCheckinOverlay
              open={aiAddGuestOpen}
              onClose={() => setAiAddGuestOpen(false)}
              onSuccess={handleAiAddGuestSuccess}
              isDark={isDark}
              title="AI Add Guest"
            />
          )}

          {/* Guest form error overlay */}
          {guestFormError && (
            <Box
              sx={{
                position: 'absolute', inset: 0, zIndex: 35,
                bgcolor: isDark ? 'rgba(0,0,0,0.68)' : 'rgba(15,23,42,0.28)',
                backdropFilter: 'blur(2px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
              }}
              onClick={() => setGuestFormError(null)}
            >
              <Paper
                onClick={(e) => e.stopPropagation()}
                sx={{
                  width: '100%', maxWidth: 420, p: 2.25, borderRadius: 2.5,
                  backgroundColor: isDark ? drawerTheme.surface : '#fff',
                  border: `1px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#fecaca'}`,
                  boxShadow: isDark ? '0 18px 42px rgba(0,0,0,0.45)' : '0 18px 42px rgba(15,23,42,0.18)',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: isDark ? drawerTheme.text : '#991b1b' }}>
                  Could not save guest
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#374151', lineHeight: 1.55, mb: 2 }}>
                  {guestFormError}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setGuestFormError(null)}
                  sx={getBookingDrawerPrimaryButtonSx(isDark)}
                >
                  OK
                </Button>
              </Paper>
            </Box>
          )}
        </Box>

        {/* Camera modal */}
        {cameraOpen && (
          <Box
            sx={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 9999,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) stopCamera(); }}
          >
            <Box sx={{
              position: 'relative', width: '100%', maxWidth: '800px',
              backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 2,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                {cameraType === 'front' ? 'Capture ID Front' : 'Capture ID Back'}
              </Typography>
              <Box sx={{
                position: 'relative', width: '100%', maxWidth: '600px',
                borderRadius: 2, overflow: 'hidden', backgroundColor: 'black',
                border: '2px solid rgba(255,255,255,0.3)',
              }}>
                <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: 'auto', display: 'block' }} />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, width: '100%' }}>
                <Button
                  variant="contained" color="primary" onClick={capturePhoto} size="large"
                  sx={{ minWidth: 150, py: 1.5, fontSize: '1rem', fontWeight: 600 }}
                >
                  Capture Photo
                </Button>
                <Button
                  variant="outlined" color="error" onClick={stopCamera} size="large"
                  sx={{
                    minWidth: 150, py: 1.5, fontSize: '1rem', fontWeight: 600,
                    borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                    '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>

      <ForeignGuestDetailsDrawer
        open={foreignGuestDrawerOpen}
        onClose={() => setForeignGuestDrawerOpen(false)}
        hotelCountry={hotelCountry}
        guestCountry={formData.country}
        checkInDate={booking?.check_in_date || null}
        initialDetails={foreignGuestDetails}
        onSave={(nextDetails) => {
          setForeignGuestDetails(nextDetails);
          if (nextDetails.passportNumber) {
            setFormData((prev) => ({
              ...prev,
              document_type: 'passport',
              id_number: String(nextDetails.passportNumber).trim(),
            }));
          }
          setValidationErrors((prev) => ({ ...prev, foreignGuest: '' }));
          setForeignGuestDrawerOpen(false);
        }}
      />

      {/* Camera permission hint snackbar */}
      <Snackbar
        open={cameraPermissionHint.open}
        autoHideDuration={9000}
        onClose={() => setCameraPermissionHint((h) => ({ ...h, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCameraPermissionHint((h) => ({ ...h, open: false }))}
          severity="warning"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {cameraPermissionHint.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BookingGuestFormDrawer;
